import { ArrowUpDown, Ban, Layers, Network } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { MoveTierLevelModal } from '../components/MoveTierLevelModal';
import { OrgUnitTypeModal } from '../components/OrgUnitTypeModal';
import { orgUnitTypesApi } from '../organizationApi';
import type { OrgUnitTypeListItem } from '../organizationTypes';

type TabKey = 'info';

/**
 * One tier on the ladder.
 *
 * It exists because **every index page in this product is partnered with a profile**, opened
 * by the anchor under the module's read key, with Edit in the row menu. "A tier is only four
 * stored values" is an argument for a THIN profile, not for none — and the partner earns its
 * keep here: the level the server computes over the whole ladder, and the count of units
 * already filed under the tier, are both what a reader wants in front of them before deciding
 * whether to move it or delete it. A paged list can show the second but never the first.
 *
 * On the **header** layout rather than the sidebar one: a tier reads as a single continuous
 * thing, not a record whose content splits into panels a reader chooses between.
 */
export function OrgTierProfilePage() {
  const { id } = useParams<{ id: string }>();
  /*
    The unit count is another module's data, gated like the index page's own `Units` column.
    Presentation only — the DTO still carries the figure — but the two surfaces must agree
    about who is shown it.
  */
  const canReadUnits = usePermission('org-units.read');

  const [tier, setTier] = useState<OrgUnitTypeListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [movingLevel, setMovingLevel] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      orgUnitTypesApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setTier(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this tier.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A plain string is a segment that goes nowhere; the two links are the way back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Organization',
    { label: 'Settings', to: '/organization/settings' },
    { label: 'Tiers', to: '/organization/settings/tiers' },
    tier?.name ?? 'Tier',
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !tier) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="org-tier-profile"
        header={
          <ProfileHeader
            id="org-tier-profile-header"
            icon={<Layers className="h-6 w-6" aria-hidden="true" />}
            title={tier.name}
            meta={[
              // A deleted tier has no position on the ladder, so there is no level to state.
              tier.level > 0 ? `Level ${tier.level}` : null,
              // Unlabelled, because "4 units" says what it is on its own.
              canReadUnits ? `${tier.unitCount} ${tier.unitCount === 1 ? 'unit' : 'units'}` : null,
            ].filter((value): value is string => value !== null)}
            status={
              <StatusText tone={tier.isActive ? 'active' : 'inactive'}>
                {tier.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={tier.createdByName}
            createdAt={tier.createdAt}
          />
        }
        banner={
          tier.isActive ? (
            <ProfileBanner
              id="org-tier-profile-banner"
              tone="slate"
              icon={<Network className="h-4 w-4" aria-hidden="true" />}
              message="This tier is on the ladder: the chart draws a column for it, and units can be filed under it."
            />
          ) : (
            <ProfileBanner
              id="org-tier-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This tier is deleted. Its column has left the chart, and the units filed under it keep resolving its name but have no column to draw in."
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="org-tier-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'org-unit-types.update',
              onClick: () => setEditing(true),
            },
            {
              key: 'move-level',
              label: 'Move level',
              icon: <ArrowUpDown className="h-4 w-4" aria-hidden="true" />,
              permission: 'org-unit-types.update',
              // State: a deleted tier is not on the ladder, so it has no level to move.
              disabledReason: tier.isActive
                ? null
                : 'Restore this tier before moving it — a deleted tier is not on the ladder.',
              onClick: () => setMovingLevel(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Tier', value: tier.name },
              { label: 'Description', value: tier.description },
              {
                /*
                  The POSITION, computed server-side over the whole ladder — which is the one
                  thing the paged list cannot state. The stored sort value behind it is
                  deliberately gapped, so printing it here would be a meaningless number
                  wearing the look of information.
                */
                label: 'Level',
                value:
                  tier.level > 0 ? (
                    <span className="font-mono tabular-nums">{tier.level}</span>
                  ) : null,
              },
              /*
                Another module's count, so it HIDES for a reader without that module's read
                key rather than rendering an em dash — a dash would say the tier holds no
                units, which is a different claim from "this is not yours to see".
              */
              ...(canReadUnits
                ? [
                    {
                      label: 'Units in this tier',
                      value: <span className="font-mono tabular-nums">{tier.unitCount}</span>,
                    },
                  ]
                : []),
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="org-tier-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: tier.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(tier.createdAt)} className="font-mono">
                    {formatDateTime(tier.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: tier.updatedByName },
              {
                label: 'Updated',
                value: tier.updatedAt ? (
                  <time dateTime={toIso(tier.updatedAt)} className="font-mono">
                    {formatDateTime(tier.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <OrgUnitTypeModal
          open
          editing={tier}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {movingLevel ? (
        <MoveTierLevelModal
          open
          tier={tier}
          onClose={() => setMovingLevel(false)}
          onMoved={() => {
            setMovingLevel(false);
            // This tier's own level is one of the ones that moved, so the record is refetched.
            load();
          }}
        />
      ) : null}
    </div>
  );
}
